// components/StoragePicker.tsx
import React from 'react';
import { AuthUser } from '../utils/authApi';

interface Props {
  user: AuthUser;
  onPick: (mode: 'local' | 'google' | 'account') => void;
  onClose?: () => void;
}

export default function StoragePicker({ user, onPick, onClose }: Props) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm relative">

        {onClose && (
          <button
            onClick={onClose}
            className="absolute -top-2 right-0 text-slate-400 hover:text-slate-700 text-3xl font-bold leading-none"
          >
            &times;
          </button>
        )}

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white font-extrabold text-lg mx-auto mb-3 shadow-lg shadow-blue-600/20">
            CA
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
            CloudAI<span className="text-blue-600">Rambo</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            Where do you want to store your data?
          </p>
        </div>

        {/* Options */}
        <div className="space-y-3">

          {/* Local Machine */}
          <button
            onClick={() => onPick('local')}
            className="w-full bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 hover:border-blue-300 hover:shadow-sm transition-all text-left group"
          >
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0 text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-slate-800">Local Machine</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                Stored in your browser's localStorage. No account needed. Data stays on this device only.
              </div>
            </div>
            <span className="shrink-0 px-3 py-1 bg-slate-100 text-slate-600 text-xs font-bold rounded-full">
              No Login
            </span>
          </button>

          {/* Google Drive */}
          <button
            onClick={() => onPick('google')}
            className="w-full bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 hover:border-blue-300 hover:shadow-sm transition-all text-left group"
          >
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-blue-50 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-slate-800">Google Drive</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                Sync invoices and client data to your Google Drive. Access from any device with your Google account.
              </div>
            </div>
            <span className="shrink-0 px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold rounded-full border border-blue-100">
              Google
            </span>
          </button>

          {/* CloudAIRambo Database */}
          <button
            onClick={() => onPick('account')}
            className="w-full bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 hover:border-purple-300 hover:shadow-sm transition-all text-left group"
          >
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-blue-50 transition-colors text-slate-500 group-hover:text-blue-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1.5 3 5 3h6c3.5 0 5-1 5-3V7c0-2-1.5-3-5-3H9C5.5 4 4 5 4 7z"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7c0 2 1.5 3 5 3h6c3.5 0 5-1 5-3"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12c0 2 1.5 3 5 3h6c3.5 0 5-1 5-3"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-slate-800">CloudAIRambo Database</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                Create an account and store everything securely on our backend. Access from any device, any browser.
              </div>
            </div>
            <span className="shrink-0 px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold rounded-full border border-blue-100">
              Account
            </span>
          </button>

        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          You can change this later in Settings.
        </p>
      </div>
    </div>
  );
}